import micromatch from 'micromatch';

/**
 * Cached glob matchers (EID-64).
 *
 * `compareObjects` previously called `micromatch.isMatch(value, pattern)` for
 * every string comparison, which re-parses and re-compiles the glob on every
 * call — and it runs once per event per trigger on the event hot path. This
 * caches the compiled matcher (`micromatch.matcher`) keyed by pattern, and
 * fast-paths patterns with no glob metacharacters to a plain `===` compare so
 * the common "literal source string" case never touches micromatch at all.
 */

// Glob metacharacters micromatch treats specially. A pattern containing none of
// these matches exactly one literal string, so we can compare with === and skip
// compilation entirely.
const GLOB_META = /[*?[\]{}()!+@|]/;

export const isGlobPattern = (pattern: string): boolean =>
  GLOB_META.test(pattern);

// pattern -> compiled matcher. Patterns are bounded by the spec (trigger
// strings) so this grows with spec size, not with event volume. Still capped
// (EID-110 M5) as a belt-and-braces bound — e.g. specs that generate
// dynamic/one-off trigger strings would otherwise grow this Map unbounded for
// the page lifetime.
const MATCHER_CACHE_MAX = 500;
const matcherCache = new Map<string, (input: string) => boolean>();

/**
 * Return a cached matcher function for `pattern`.
 *
 * For literal (non-glob) patterns the returned function is a plain equality
 * check; for glob patterns it is a `micromatch.matcher`, compiled once and
 * reused for every subsequent call with the same pattern.
 */
export const getMatcher = (pattern: string): ((input: string) => boolean) => {
  const cached = matcherCache.get(pattern);
  if (cached) {
    // Touch: re-insert so `pattern` becomes most-recently-used (Map
    // preserves insertion order), so eviction below drops the LRU entry.
    matcherCache.delete(pattern);
    matcherCache.set(pattern, cached);
    return cached;
  }
  const matcher = isGlobPattern(pattern)
    ? micromatch.matcher(pattern)
    : (input: string) => input === pattern;
  matcherCache.set(pattern, matcher);
  if (matcherCache.size > MATCHER_CACHE_MAX) {
    const oldestKey = matcherCache.keys().next().value as string;
    matcherCache.delete(oldestKey);
  }
  return matcher;
};

/**
 * Test whether `value` matches `pattern`, using (and populating) the matcher
 * cache. Equivalent to `micromatch.isMatch(value, pattern)` for the inputs the
 * event bus produces, but without recompiling the glob on every call.
 */
export const matchPattern = (value: string, pattern: string): boolean =>
  getMatcher(pattern)(value);

/** Clear the matcher cache. Exposed for tests. */
export const clearMatcherCache = (): void => {
  matcherCache.clear();
};

/** Current number of cached matchers. Exposed for tests. */
export const matcherCacheSize = (): number => matcherCache.size;
